import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, CheckCheck, ChevronDown, Megaphone, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { AnnouncementsDialog } from "./Announcements";

const when = (iso) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const GO = { leave: "Open my leave", leave_request: "Review leave", attendance: "Check my attendance", attendance_check: "Check my attendance", attendance_request: "Review request",
  payroll: "View payslip", reward: "See bonus & incentives", letter: "Open letters", request: "Open letters", announcement: "Open" };

function AlertView({ n, onClose, onGo }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-full" data-testid="hr-alert-view">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-violet-600 flex items-center gap-1.5">{n.kind === "announcement" ? <><Megaphone className="h-3.5 w-3.5" />Announcement</> : "HR alert"}</div>
          <DialogTitle className="font-heading text-xl text-left leading-snug" data-testid="hr-alert-subject">{n.title}</DialogTitle>
          <DialogDescription className="text-left">{when(n.created_at)} · <span className="text-emerald-700 font-semibold" data-testid="hr-alert-read-state">Marked as read</span></DialogDescription>
        </DialogHeader>
        <div className="text-sm text-slate-800 whitespace-pre-line leading-relaxed max-h-[50vh] overflow-y-auto" data-testid="hr-alert-message">{n.body}</div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} data-testid="hr-alert-close">Close</Button>
          {n.link && <Button onClick={() => onGo(n.link)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="hr-alert-go">{GO[n.kind] || "Go to"}<ArrowRight className="h-4 w-4 ml-1" /></Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AlertRow({ n, onOpen }) {
  return (
    <button onClick={() => onOpen(n)} className={`w-full text-left px-3 py-2.5 flex gap-2.5 transition-colors ${n.read ? "hover:bg-slate-50" : "bg-violet-50 hover:bg-violet-100/70"}`} data-testid={`hr-alert-${n.id}`}>
      <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-violet-600"}`} />
      <span className="min-w-0 flex-1">
        <span className={`block text-sm truncate ${n.read ? "text-slate-700" : "font-bold text-slate-900"}`}>{n.title}</span>
        <span className="block text-xs text-slate-500 line-clamp-2">{n.body}</span>
        <span className="block text-[10px] text-slate-400 mt-0.5">{when(n.created_at)}</span>
      </span>
    </button>
  );
}

export function HrBell() {
  const { user } = useAuth();
  const isHr = user?.role === "admin" || user?.role === "hr" || !!user?.custom_role?.hr_manage || user?.mobile === "9243425500";
  const nav = useNavigate();
  const [data, setData] = useState({ unread: 0, total: 0, items: [] });
  const [limit, setLimit] = useState(5);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [announce, setAnnounce] = useState(false);
  const load = useCallback(() => api.get(`/hr/notifications?limit=${limit}`).then(({ data: d }) => setData(d)).catch(() => {}), [limit]);
  useEffect(() => {
    load();
    const t = setInterval(() => !document.hidden && load(), 60_000);
    return () => clearInterval(t);
  }, [load]);
  const openAlert = (n) => {
    setOpen(false);
    setView(n);
    if (!n.read) api.post(`/hr/notifications/${n.id}/read`).then(load).catch(() => {});
  };
  const readAll = () => api.post("/hr/notifications/read").then(load).catch(() => {});
  return (
    <div className="relative shrink-0">
      <button onClick={() => setOpen((o) => !o)} className={`relative h-9 w-9 rounded-md inline-flex items-center justify-center transition-colors ${data.unread ? "bg-violet-100 text-violet-700" : "hover:bg-violet-50 text-violet-600"}`} title="HR alerts" data-testid="hr-bell-btn">
        <Briefcase className="h-4 w-4" />
        {data.unread > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white animate-pulse" data-testid="hr-bell-badge">{data.unread > 99 ? "99+" : data.unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed left-2 right-2 top-14 sm:absolute sm:left-auto sm:top-auto sm:right-0 mt-2 sm:w-[340px] z-50 bg-white rounded-xl border border-slate-200 shadow-2xl overflow-hidden" data-testid="hr-bell-panel">
            <div className="px-3 py-2 flex items-center gap-2 bg-slate-50 border-b">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex-1">HR alerts {data.unread > 0 && <span className="text-violet-600">· {data.unread} new</span>}</span>
              {data.unread > 0 && <button onClick={readAll} className="text-[11px] font-semibold text-violet-700 hover:underline inline-flex items-center gap-1" data-testid="hr-bell-read-all"><CheckCheck className="h-3.5 w-3.5" />Mark all read</button>}
              {isHr && <button onClick={() => { setOpen(false); setAnnounce(true); }} className="text-[11px] font-semibold text-slate-700 hover:text-violet-700 inline-flex items-center gap-1" data-testid="hr-bell-announce"><Megaphone className="h-3.5 w-3.5" />Announce</button>}
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
              {data.items.length === 0 ? <div className="p-6 text-center text-sm text-slate-500">No alerts yet.</div> : data.items.map((n) => <AlertRow key={n.id} n={n} onOpen={openAlert} />)}
            </div>
            {data.total > data.items.length && (
              <button onClick={() => setLimit((l) => l + 10)} className="w-full py-2 text-xs font-semibold text-violet-700 hover:bg-violet-50 border-t inline-flex items-center justify-center gap-1" data-testid="hr-bell-show-more">
                Show more ({data.total - data.items.length}) <ChevronDown className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </>
      )}
      {view && <AlertView n={view} onClose={() => setView(null)} onGo={(l) => { setView(null); nav(l); }} />}
      {announce && <AnnouncementsDialog onClose={() => { setAnnounce(false); load(); }} />}
    </div>
  );
}
